import React, { useMemo } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { HoverUnderlineSpan } from "@/components/ui/HoverUnderlineSpan";
import { useFormatting } from "@/hooks/useFormatting";
import { useDataSourceContext } from "@/contexts/DataSourceContext";
import { getDashboardMetrics } from "@/services/metricsService";
import { useDrillDown, generate90DayForecastDrillDown } from "@/hooks/useDrillDown";
import { usePermissions } from "@/hooks/usePermissions";
import { useApp } from "@/contexts/AppContext";
import { useBaseForecast } from "@/hooks/useBaseForecast";

export default function Ceo90DayForecastCard() {
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useFormatting();
  const { dataSources } = useDataSourceContext();
  const { showDrillDown } = useDrillDown();
  const { mode } = useApp();
  const { baseForecast, metrics: forecastMetrics, isLoading, error } = useBaseForecast();

  const demoMetrics = useMemo(() => {
    if (mode === "live") return null;
    return getDashboardMetrics(dataSources);
  }, [dataSources, mode]);

  const liveValues = useMemo(() => {
    if (mode !== "live" || !forecastMetrics) return null;
    const expected90DayIncome = baseForecast.reduce((sum, dp) => sum + dp.incoming, 0);
    const expected90DayExpenses = baseForecast.reduce((sum, dp) => sum + dp.outgoing, 0);
    return {
      currentCash: forecastMetrics.startingCash,
      projectedCash90Days: forecastMetrics.endingCash,
      expected90DayIncome,
      expected90DayExpenses,
    };
  }, [mode, forecastMetrics, baseForecast]);

  if (!hasPermission("view_executive_cash_position_card")) return null;

  if (mode === "live" && isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading forecast data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (mode === "live" && error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-destructive">{error}</div>
        </CardContent>
      </Card>
    );
  }

  const currentCash = mode === "live" ? liveValues?.currentCash ?? 0 : demoMetrics?.currentCash ?? 0;
  const projectedCash90Days = mode === "live" ? liveValues?.projectedCash90Days ?? 0 : demoMetrics?.projectedCash90Days ?? 0;
  const expected90DayIncome = mode === "live" ? liveValues?.expected90DayIncome ?? 0 : demoMetrics?.expected90DayIncome ?? 0;
  const expected90DayExpenses = mode === "live" ? liveValues?.expected90DayExpenses ?? 0 : demoMetrics?.expected90DayExpenses ?? 0;

  const change = projectedCash90Days - currentCash;
  const changePercent = currentCash !== 0 ? (change / Math.abs(currentCash)) * 100 : 0;
  const isPositive = changePercent >= 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;

  const formatPercentage = (value: number) => {
    const formatted = Math.abs(value).toFixed(1);
    return isPositive ? `+${formatted}%` : `-${formatted}%`;
  };

  return (
    <Card className="h-full" style={{ borderLeft: '3px solid var(--color-info-400)' }}>
      <CardContent className="p-4 flex flex-col h-full">
        {/* Top row — title + icon badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-base font-medium text-neutral-800 truncate">90-Day Forecast</span>
            <InfoTooltip
              title="90-Day Forecast"
              content="Projected cash position 90 days from now based on outstanding invoices, bills, and scheduled payments. Useful for medium-term planning."
            />
          </div>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--color-info-100)' }}>
            <TrendIcon className="h-4 w-4" style={{ color: 'var(--color-info-600)' }} />
          </div>
        </div>

        {/* Value */}
        <div className="mt-3">
          <HoverUnderlineSpan
            className="text-2xl font-bold text-neutral-900 tracking-tight cursor-pointer"
            onClick={() =>
              showDrillDown(
                "forecast-point",
                generate90DayForecastDrillDown(
                  projectedCash90Days,
                  currentCash,
                  expected90DayIncome,
                  expected90DayExpenses,
                ),
              )
            }
          >
            {formatCurrency(projectedCash90Days)}
          </HoverUnderlineSpan>
        </div>

        {/* Sub-text */}
        <div className="mt-1 flex items-center gap-1">
          <span className="text-xs font-semibold text-neutral-800">{formatPercentage(changePercent)}</span>
          <span className="text-xs font-normal text-neutral-800">vs current</span>
        </div>
      </CardContent>
    </Card>
  );
}
